import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Chip, Stack, Select, MenuItem, FormControl, InputLabel, Button, SelectChangeEvent } from '@mui/material';
import { Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { getMonthlyReport } from '../services/financeService';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PaymentsIcon from '@mui/icons-material/Payments';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { PageContainer, PageHeader, SectionCard, EmptyState, StatCard, ResponsiveTable } from '../components/ui';
import type { Column } from '../components/ui';
import { brand } from '../theme/tokens';

interface StaffInfo {
  displayName: string;
  [key: string]: unknown;
}

interface PlanInfo {
  _id: string;
  name: string;
  zh_name: string;
  [key: string]: unknown;
}

interface Reservation {
  _id: string;
  staffId: StaffInfo;
  planId?: PlanInfo;
  createdType?: string;
  paymentType?: string;
  originalAmount: number;
  start: string;
  status: string;
  [key: string]: unknown;
}

interface ReportFilters {
  stylistName: string;
  plan: string;
  createdType: string;
  paymentType: string;
  status: string;
}

function MonthlyReport(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [cutoffDate, setCutoffDate] = useState<Dayjs>(dayjs());
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState<number>(0);
  const [fromDate, setFromDate] = useState<Dayjs>(dayjs().subtract(1, 'month').date(dayjs().date()));
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [filters, setFilters] = useState<ReportFilters>({
    stylistName: '',
    plan: '',
    createdType: '',
    paymentType: '',
    status: '',
  });

  const calculateMonthlyRevenue = useCallback((reservations: Reservation[]) => {
    const revenue = reservations.reduce((total, transaction) => total + transaction.originalAmount, 0);
    setMonthlyRevenue(revenue);
  }, [setMonthlyRevenue]);

  const fetchReservations = useCallback(async (date: Dayjs) => {
    const startDate = date.subtract(1, 'month').date(date.date());
    const adjustedStartDate = startDate.isValid() ? startDate : startDate.endOf('month');
    setFromDate(adjustedStartDate);

    setIsLoading(true);
    try {
      const reportResponse = await getMonthlyReport(adjustedStartDate.toISOString(), date.toISOString());
      console.log('Fetched reservations:', reportResponse);
      const fetchedReservations = Array.isArray(reportResponse.report) ? reportResponse.report as Reservation[] : [];
      setReservations(fetchedReservations);
      calculateMonthlyRevenue(fetchedReservations);
    } catch (error) {
      console.error("Error fetching reservations:", error);
    } finally {
      setIsLoading(false);
    }
  }, [setFromDate, setReservations, calculateMonthlyRevenue]);

  useEffect(() => {
    fetchReservations(cutoffDate);
  }, [cutoffDate, fetchReservations]);

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setCutoffDate(date);
      fetchReservations(date);
    }
  };

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    console.log('Filter changed:', name, value);
    setFilters((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [name as string]: value,
      };
      // Recalculate revenue based on new filters
      const newFilteredReservations = reservations.filter((reservation) => {
        return (
          (newFilters.stylistName === '' || (reservation.staffId?.displayName?.includes(newFilters.stylistName) ?? false)) &&
          (newFilters.plan === '' || (reservation.planId?._id?.includes(newFilters.plan) ?? false)) &&
          (newFilters.createdType === '' || (reservation.createdType?.includes(newFilters.createdType) ?? false)) &&
          (newFilters.paymentType === '' || (reservation.paymentType?.includes(newFilters.paymentType) ?? false)) &&
          (newFilters.status === '' || reservation.status.includes(newFilters.status))
        );
      });
      calculateMonthlyRevenue(newFilteredReservations);
      return newFilters;
    });
  };

  const filteredReservations = reservations.filter((reservation) => {
    return (
      (filters.stylistName === '' || (reservation.staffId?.displayName?.includes(filters.stylistName) ?? false)) &&
      (filters.plan === '' || (reservation.planId?._id?.includes(filters.plan) ?? false)) &&
      (filters.createdType === '' || (reservation.createdType?.includes(filters.createdType) ?? false)) &&
      (filters.paymentType === '' || (reservation.paymentType?.includes(filters.paymentType) ?? false)) &&
      (filters.status === '' || reservation.status.includes(filters.status))
    );
  });

  const getUniqueValues = useCallback((key: string): string[] => {
    return [...new Set(reservations.map(reservation => {
      const keys = key.split('.');
      let result: unknown = reservation;
      for (const curr of keys) {
        result = result && (result as Record<string, unknown>)[curr];
      }
      return result as string;
    }))];
  }, [reservations]);

  const downloadCSV = () => {
    const reservationData: Array<Record<string, string | number>> = filteredReservations.map(reservation => ({
      'Stylist Name': reservation.staffId?.displayName ?? '',
      'Plan': language === 'zh' ? (reservation.planId?.zh_name ?? '') : (reservation.planId?.name ?? ''),
      'Created Type': reservation.createdType ?? '',
      'Payment Type': reservation.paymentType ?? '',
      'Amount': reservation.originalAmount ?? 0,
      'Date': dayjs(reservation.start).format('YYYY-MM-DD'),
      'Status': reservation.status ?? '',
    }));

    // Add a row for the monthly revenue
    reservationData.push({
      'Stylist Name': '',
      'Plan': '',
      'Created Type': '',
      'Payment Type': '',
      'Amount': `Total Revenue: $${monthlyRevenue}`,
      'Date': '',
      'Status': '',
    });

    const worksheet = XLSX.utils.json_to_sheet(reservationData);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `MonthlyReport_${cutoffDate.format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(t('monthlyReport.downloadSuccess', 'Report downloaded'));
  };

  const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    const s = (status || '').toLowerCase();
    if (s.includes('complete') || s.includes('confirm') || s.includes('paid')) return 'success';
    if (s.includes('pending') || s.includes('wait')) return 'warning';
    if (s.includes('cancel') || s.includes('reject') || s.includes('fail')) return 'error';
    return 'default';
  };

  const columns: Column<Reservation>[] = [
    {
      key: 'stylist',
      header: t('reservationList.stylist'),
      primary: true,
      render: (r) => r.staffId?.displayName ?? '',
    },
    {
      key: 'plan',
      header: t('reservationList.plan'),
      render: (r) => (language === 'zh' ? (r.planId?.zh_name ?? '') : (r.planId?.name ?? '')),
    },
    {
      key: 'amount',
      header: t('reservationList.originalAmount'),
      align: 'right',
      render: (r) => (
        <Typography component="span" sx={{ fontWeight: 700, color: brand.goldDark }}>
          ${r.originalAmount}
        </Typography>
      ),
    },
    {
      key: 'date',
      header: t('reservationList.date'),
      render: (r) => dayjs(r.start).format('YYYY-MM-DD'),
    },
    {
      key: 'status',
      header: t('reservationList.status'),
      render: (r) => <Chip size="small" label={r.status} color={statusColor(r.status)} sx={{ fontWeight: 600 }} />,
    },
    {
      key: 'paymentType',
      header: t('reservationList.paymentType'),
      render: (r) => r.paymentType ?? '',
    },
    {
      key: 'createdType',
      header: t('reservationList.createdType'),
      render: (r) => r.createdType ?? '',
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <PageContainer>
        <PageHeader
          title={t('monthlyReport.title', 'Monthly Report')}
          subtitle={t('monthlyReport.subtitle', 'Revenue and reservations for the selected period.')}
          icon={<AssessmentIcon />}
          classicPath="/classic/monthly-report"
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={downloadCSV}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {t('monthlyReport.downloadCSV')}
            </Button>
          }
        />

        <Stack spacing={2.5}>
          {/* Filters */}
          <SectionCard title={t('monthlyReport.filters', 'Filters')} icon={<FilterListIcon />}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <DatePicker
                  label={t('monthlyReport.cutoffDate')}
                  value={cutoffDate}
                  onChange={handleDateChange}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('monthlyReport.stylistName')}</InputLabel>
                  <Select
                    label={t('monthlyReport.stylistName')}
                    name="stylistName"
                    value={filters.stylistName}
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">{t('common.all')}</MenuItem>
                    {getUniqueValues('staffId.displayName').map((name, index) => (
                      <MenuItem key={`stylist-${index}`} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('monthlyReport.plan')}</InputLabel>
                  <Select
                    label={t('monthlyReport.plan')}
                    name="plan"
                    value={filters.plan}
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">{t('common.all')}</MenuItem>
                    {getUniqueValues('planId._id').map((plan, index) => (
                      <MenuItem key={`plan-${index}`} value={plan}>
                        {language === 'zh' ? (reservations.find(res => res.planId?._id === plan)?.planId?.zh_name ?? plan) : plan}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('monthlyReport.createdType')}</InputLabel>
                  <Select
                    label={t('monthlyReport.createdType')}
                    name="createdType"
                    value={filters.createdType}
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">{t('common.all')}</MenuItem>
                    {getUniqueValues('createdType').map((type, index) => (
                      <MenuItem key={`createdType-${index}`} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('monthlyReport.paymentType')}</InputLabel>
                  <Select
                    label={t('monthlyReport.paymentType')}
                    name="paymentType"
                    value={filters.paymentType}
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">{t('common.all')}</MenuItem>
                    {getUniqueValues('paymentType').map((type, index) => (
                      <MenuItem key={`paymentType-${index}`} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('monthlyReport.status')}</InputLabel>
                  <Select
                    label={t('monthlyReport.status')}
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">{t('common.all')}</MenuItem>
                    {getUniqueValues('status').map((status, index) => (
                      <MenuItem key={`status-${index}`} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('monthlyReport.fromDate')}: <strong>{fromDate.format('YYYY-MM-DD')}</strong>
              {'  '}&rarr;{'  '}
              {t('monthlyReport.toDate')}: <strong>{cutoffDate.format('YYYY-MM-DD')}</strong>
            </Typography>
          </SectionCard>

          {/* Revenue KPI */}
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <StatCard
                tone="gold"
                icon={<PaymentsIcon />}
                label={t('monthlyReport.monthlyRevenue')}
                value={`$${monthlyRevenue}`}
                hint={t('monthlyReport.revenueHint', '{{count}} reservation(s)', { count: filteredReservations.length })}
              />
            </Grid>
          </Grid>

          {/* Reservations table */}
          <SectionCard title={t('monthlyReport.reservations', 'Reservations')} icon={<ReceiptLongIcon />} disableContentPadding contentSx={{ p: { xs: 1.5, sm: 2 } }}>
            <ResponsiveTable<Reservation>
              columns={columns}
              rows={filteredReservations}
              getRowKey={(row) => row._id}
              loading={isLoading}
              emptyState={
                <EmptyState
                  icon={<AssessmentIcon />}
                  title={t('monthlyReport.emptyTitle', 'No reservations found')}
                  description={t('monthlyReport.emptyDescription', 'No reservations match the selected period and filters.')}
                  dense
                />
              }
            />
          </SectionCard>
        </Stack>
      </PageContainer>
    </LocalizationProvider>
  );
}

export default MonthlyReport;
